import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import {
  ArchiveUmmeldungRequestParams,
  CreateUmmeldungRequestParams,
  DeleteUmmeldungRequestParams,
  FindUmmeldungenRequestParams,
  GetUmmeldungLockStatusRequestParams,
  GetUmmeldungPdfRequestParams,
  GetUmmeldungRequestParams,
  LockUmmeldungRequestParams,
  RefreshUmmeldungLockRequestParams,
  SubmitUmmeldungRequestParams,
  UmmeldungServiceInterface,
  UnlockUmmeldungRequestParams,
  UpdateUmmeldungRequestParams,
  UploadUmmeldungPdfRequestParams,
} from '../../../generated/api/ummeldung.serviceInterface';
import { PagedUmmeldungViewResponse, UmmeldungLockStatusDto, UmmeldungResponse } from '../../../generated/model/models';
import { Observable } from 'rxjs';
import { Configuration } from '../../../generated/configuration';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { API_ROUTE_UMMELDUNG, API_ROUTE_UMMELDUNG_LOCK, getApiUrl } from '../../app.constants';
import { UmmeldungLockRequestParams } from '../../model/ummeldung.model';

@Injectable({
  providedIn: 'root',
})
export class UmmeldungService extends ApiService implements UmmeldungServiceInterface {
  readonly defaultHeaders = new HttpHeaders();
  readonly configuration = new Configuration();

  archiveUmmeldung(requestParameters: ArchiveUmmeldungRequestParams): Observable<object> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG) + `/${requestParameters.id}/archive`;

    return this.httpClient.put(apiPath, null);
  }

  createUmmeldung(requestParameters: CreateUmmeldungRequestParams): Observable<string> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG);
    return this.httpClient.post<string>(apiPath, requestParameters.ummeldungRequest);
  }

  deleteUmmeldung(requestParameters: DeleteUmmeldungRequestParams): Observable<object> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG) + `/${requestParameters.id}`;
    return this.httpClient.delete(apiPath);
  }

  findUmmeldungen(requestParameters: FindUmmeldungenRequestParams): Observable<PagedUmmeldungViewResponse> {
    const apiPath = getApiUrl(this.apiUrl, `${API_ROUTE_UMMELDUNG}/find`);
    let params = new HttpParams();

    if (requestParameters.page !== undefined) {
      params = params.set('page', requestParameters.page);
    }

    if (requestParameters.size !== undefined) {
      params = params.set('size', requestParameters.size);
    }

    return this.httpClient.post<PagedUmmeldungViewResponse>(apiPath, requestParameters.ummeldungFilter ?? null, { params });
  }

  getUmmeldung(requestParameters: GetUmmeldungRequestParams): Observable<UmmeldungResponse> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG) + `/${requestParameters.id}`;
    return this.httpClient.get<UmmeldungResponse>(apiPath);
  }

  getUmmeldungPdf(requestParameters: GetUmmeldungPdfRequestParams): Observable<Blob> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG) + `/${requestParameters.id}/pdf`;
    return this.httpClient.get(apiPath, {
      responseType: 'blob',
    });
  }

  submitUmmeldung(requestParameters: SubmitUmmeldungRequestParams): Observable<object> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG) + `/${requestParameters.id}/submit`;
    return this.httpClient.put(apiPath, null);
  }

  updateUmmeldung(requestParameters: UpdateUmmeldungRequestParams): Observable<object> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG) + `/${requestParameters.id}`;
    return this.httpClient.put(apiPath, requestParameters.ummeldungRequest);
  }

  uploadUmmeldungPdf(requestParameters: UploadUmmeldungPdfRequestParams, extraHttpRequestparams): Observable<object> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG) + `/${requestParameters.id}/pdf`;
    const formData: FormData = new FormData();
    formData.append('file', requestParameters.file, extraHttpRequestparams.filename);
    return this.httpClient.put(apiPath, formData);
  }

  getUmmeldungLockStatus(requestParameters: GetUmmeldungLockStatusRequestParams): Observable<UmmeldungLockStatusDto> {
    return this.httpClient.get(this.createUmmeldungLockApiPath(requestParameters));
  }

  lockUmmeldung(requestParameters: LockUmmeldungRequestParams): Observable<UmmeldungLockStatusDto> {
    return this.httpClient.post(this.createUmmeldungLockApiPath(requestParameters), null);
  }

  refreshUmmeldungLock(requestParameters: RefreshUmmeldungLockRequestParams): Observable<UmmeldungLockStatusDto> {
    return this.httpClient.put(this.createUmmeldungLockApiPath(requestParameters), null);
  }

  unlockUmmeldung(requestParameters: UnlockUmmeldungRequestParams): Observable<object> {
    return this.httpClient.delete(this.createUmmeldungLockApiPath(requestParameters));
  }

  private createUmmeldungLockApiPath(requestParams: UmmeldungLockRequestParams) {
    return `${getApiUrl(this.apiUrl, API_ROUTE_UMMELDUNG_LOCK)}/${requestParams.vertragsnummer}/${requestParams.zaehlpunktnummer}`;
  }
}
